import { Box, Grid, useTheme } from '@wegroup/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getGuaranteeNameForKey } from '../../../../../services/productFactoryService';
import { toEuro } from '../../../../../utils/stringUtils';
import useIsMultiQuoteContractFlow from '../../../hooks/useIsMultiQuoteContractFlow';
import useOffer from '../../../store/offer/useOffer';
import useQuoteSpecs from '../../../store/offer/useQuoteSpecs';
import { OFFER_RIGHT_SIDE_WIDTH } from '../offer-result/OfferResult';
import OfferCloseOnline from './OfferCloseOnline';
import OfferCompareAction from './OfferCompareAction';
import OfferMailCalculationAction from './OfferMailCalculationAction';
import OfferParametersChangeAction from './OfferParametersChangeAction';
import OfferRecalculateAction from './OfferRecalculateAction';
import OfferSelectQuoteAction from './OfferSelectQuoteAction';

export type EnabledOfferActions = 'COMPARE';

interface Props {
  enabledActions?: EnabledOfferActions[];
  /** When true, the quote will be stored in selectedQuotesStore instead */
  isSelectable?: boolean;
}

const OfferActions: React.FC<Props> = ({
  enabledActions = [],
  isSelectable,
}) => {
  const { t } = useTranslation();
  const { sizes } = useTheme();
  const { offer, isLoading, isFetched, isError, isFetching } = useOffer();
  const { quoteSpecs, setQuoteSpecs } = useQuoteSpecs();
  const { fetchQuote } = useOffer();
  const isMultiQuoteSelect = useIsMultiQuoteContractFlow();

  return (
    <Grid
      gridTemplateColumns={{
        base: '1fr',
        mobileL: '1fr 1fr',
        laptop: `1fr 1fr 1fr ${sizes[OFFER_RIGHT_SIDE_WIDTH]}`,
      }}
      borderTop="1px"
      borderColor="gray.50"
      // We use a CSS trick to add inner borders
      bg="gray.50"
      gap="1px"
    >
      <OfferParametersChangeAction
        offer={offer}
        quoteSpecs={quoteSpecs}
        onQuoteSpecsChange={setQuoteSpecs}
        isDisabled={isLoading}
      />
      <OfferCompareAction
        offer={offer}
        isDisabled={!enabledActions.includes('COMPARE') || !isFetched}
      />
      <OfferMailCalculationAction
        offerId={offer.id!}
        isDisabled={!isFetched}
        isHidden={offer.info.insurance_type === 'travel'}
        isExtendedInput={
          ['villasure'].includes(offer?.info?.company?.key) ||
          offer?.info?.insurance_type === 'boat'
        }
      />
      <Box w={{ base: '100%', laptop: OFFER_RIGHT_SIDE_WIDTH }} flexShrink={0}>
        {isError ? (
          <OfferRecalculateAction onClick={fetchQuote} isLoading={isFetching} />
        ) : isSelectable ? (
          <OfferSelectQuoteAction
            selectedQuote={{
              id: offer.id || '',
              name: getGuaranteeNameForKey(
                offer.info.insurance_name,
                offer.guarantee_content,
              ),
              price: offer.premium
                ? toEuro(offer.premium.total_premium, offer.premium.currency)
                : '',
              company_key: offer.info.company.key,
              company_name: offer.info.company?.name || t('Insurer'),
            }}
          />
        ) : (
          <OfferCloseOnline
            offerId={offer.id}
            offerIds={isMultiQuoteSelect ? [offer.id!] : undefined}
          />
        )}
      </Box>
    </Grid>
  );
};

export default OfferActions;
